import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DynamicIcon from "../DynamicIcon";
import { closeModal, setHasToRefresh, setModal, setSwap } from "../../redux/rightModalSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import LoadingSection from "../LoadingSection";
import { useColorPalette } from "../../hooks/useColorPalette";
import Folder from "../templates/folder/Folder";
import LocationMap from "./LocationMap";
import DistanceInfo from "./DistanceInfo";
import moment from "moment";
import 'moment/locale/it'; // Importa la localizzazione italiana
import axios from "axios";
import AppointmentCard from "../AppointmentCard";
import { getNodeApiUrl } from "../../config/api.config";
import { faCalendarAlt, faPencil, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import AttachmentsViewer from "../AttachmentsViewer";
import JobMaterialsPanel from "./JobMaterialsPanel";

// Componente per visualizzare una coppia etichetta-valore con stile
const DetailItem = ({ label, value, palette, isExpandable = false, totalWorkedMinutes = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const MAX_WORDS = 30; // Numero di parole da visualizzare prima del troncamento

    const toggleIsExpanded = (e) => {
        e.preventDefault();
        setIsExpanded(!isExpanded);
    };

    const renderValue = () => {
        if (isExpandable && typeof value === 'string') {
            const words = value.split(/\s+/); // Splitta per spazi multipli o singolii
            if (words.length > MAX_WORDS) {
                const linkStyle = { color: palette.primary || '#007bff', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.75rem', display: 'inline-block', marginLeft: '4px' };
                if (isExpanded) {
                    return (
                        <>
                            {value}
                            <span onClick={toggleIsExpanded} style={linkStyle} role="button" tabIndex={0} onKeyPress={(e) => e.key === 'Enter' && toggleIsExpanded(e)}>
                                Leggi di meno
                            </span>
                        </>
                    );
                }
                return (
                    <>
                        {words.slice(0, MAX_WORDS).join(' ')}...
                        <span onClick={toggleIsExpanded} style={linkStyle} role="button" tabIndex={0} onKeyPress={(e) => e.key === 'Enter' && toggleIsExpanded(e)}>
                            Leggi di più
                        </span>
                    </>
                );
            }
        }

        // Gestione specifica per "Ore Previste" con confronto rispetto alle ore lavorate
        if (label === "Ore Previste" && value && totalWorkedMinutes > 0) {
            const expectedHours = parseFloat(value);
            
            // Calcola la differenza in minuti: ore lavorate - ore previste
            const expectedMinutes = expectedHours * 60;
            const diffMinutes = totalWorkedMinutes - expectedMinutes;
            
            // Converti la differenza in ore e minuti
            const absDiffMinutes = Math.abs(diffMinutes);
            const diffHours = Math.floor(absDiffMinutes / 60);
            const remainingMinutes = absDiffMinutes % 60;
            
            // Formatta la differenza
            let diffFormatted = '';
            if (diffHours > 0 && remainingMinutes > 0) {
                diffFormatted = `${diffHours}h ${remainingMinutes}m`;
            } else if (diffHours > 0) {
                diffFormatted = `${diffHours}h`;
            } else if (remainingMinutes > 0) {
                diffFormatted = `${remainingMinutes}m`;
            } else {
                diffFormatted = '0m';
            }
            
            // Mostra il segno della differenza
            const diffSign = diffMinutes > 0 ? '+' : diffMinutes < 0 ? '-' : '';
            
            // Stile per il pallino colorato
            // Pallino rosso quando il totale è positivo (ore lavorate > ore previste)
            // Pallino verde quando il totale è negativo o uguale (ore lavorate ≤ ore previste)
            const dotStyle = {
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                display: 'inline-block',
                marginLeft: '6px',
                marginRight: '6px',
                backgroundColor: diffMinutes <= 0 ? 'green' : 'red'
            };
            
            return (
                <div className="flex items-center">
                    {value || '--'}
                    <span style={dotStyle}></span>
                    <span style={{ fontSize: '0.95rem', fontWeight: '500' }}>({diffSign}{diffFormatted})</span>
                </div>
            );
        }

        return value || '--';
    };

    return (
        <div className="py-2">
            <p className="text-sm font-medium" style={{ color: palette.textOnBackground, opacity: 0.7 }}>{label}</p>
            <div className="text-base font-semibold" style={{ color: palette.textOnBackground, whiteSpace: 'pre-wrap' }}>
                {renderValue()}
            </div>
        </div>
    );
};

export default function ViewJob({ jobId, appointmentId, calendarView }) {
    const palette = useColorPalette();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');

    // Stati principali
    const [jobData, setJobData] = useState(null);
    const [systemData, setSystemData] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [assistanceData, setAssistanceData] = useState(null);

    // Stati di caricamento
    const [loading, setLoading] = useState(true);
    const [systemLoading, setSystemLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteInProgress, setDeleteInProgress] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [deleteMode, setDeleteMode] = useState('single'); // 'single' o 'group'
    const [showFullNotes, setShowFullNotes] = useState(false); // Stato per mostrare/nascondere note complete
    
    // Funzione per ottenere il colore dallo statusId della commessa
    const getJobStatusInfo = (statusId) => {
        if (!statusId || !accountData?.settings?.jobStatus) {
            return {
                label: 'N/D',
                color: '#6c757d' // Default: grigio
            };
        }
        
        const matchingStatus = accountData.settings.jobStatus.find(status => status._id === statusId);
        
        if (matchingStatus) {
            return {
                label: matchingStatus.label,
                color: matchingStatus.color || '#6c757d'
            };
        }
        
        return {
            label: 'N/D',
            color: '#6c757d' // Default: grigio
        };
    };
    const [appointmentsLoading, setAppointmentsLoading] = useState(false);
    const [assistanceLoading, setAssistanceLoading] = useState(false);

    // Stati di errore
    const [error, setError] = useState(null);
    const [systemError, setSystemError] = useState(null); // Aggiunto stato per errore impianto
    const [appointmentsError, setAppointmentsError] = useState(null);
    const [assistanceError, setAssistanceError] = useState(null);
    const [totalWorkTime, setTotalWorkTime] = useState({ hours: 0, minutes: 0 });
    const [userWorkSummary, setUserWorkSummary] = useState([]);
    const [totalWorkedMinutes, setTotalWorkedMinutes] = useState(0);
    
    // Stati UI
    const [downloadingAllFiles, setDownloadingAllFiles] = useState(false); // Spostato al livello superiore

    // Gestione tab
    const [initialTab, setInitialTab] = useState(0);
    const handleTabChange = (index) => {
        setInitialTab(index);
    };

    const [expandedAppointmentId, setExpandedAppointmentId] = useState(null);

    const toggleExpandAppointment = (appointmentId) => {
        setExpandedAppointmentId(prevId => (prevId === appointmentId ? null : appointmentId));
    };

    moment.locale('it');

    // Fetch dei dati principali della commessa
    useEffect(() => {
        const fetchJobData = async () => {
            if (!jobId) return;
            setLoading(true);
            setShowFullNotes(false); // Reset stato note quando cambia commessa
            try {
                const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/jobs`, {
                    headers: { 'auth-token': token },
                    params: { jobId }
                });
                const data = response.data?.ret || response.data;
                if (!data?._id) throw new Error("Dati della commessa non validi");
                setJobData(data);
            } catch (err) {
                setError("Impossibile caricare i dettagli della commessa.");
            } finally {
                setLoading(false);
            }
        };
        fetchJobData();
    }, [jobId, token]);

    // Fetch dei dati dell'impianto collegato
    useEffect(() => {
        if (!jobData?.systemId) return;
        const fetchSystemData = async () => {
            setSystemLoading(true);
            setSystemError(null); // Resetta l'errore precedente
            try {
                const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/systems`, {
                    headers: { 'auth-token': token },
                    params: { systemId: jobData.systemId }
                });
                const data = response.data?.ret || response.data;
                setSystemData(data);
            } catch (err) {
                console.error("Errore caricamento dati impianto:", err);
                setSystemError("Impossibile caricare i dettagli aggiuntivi dell'impianto.");
            } finally {
                setSystemLoading(false);
            }
        };
        fetchSystemData();
    }, [jobData, token]);
    
    // Fetch dei dati della richiesta di assistenza collegata (se presente)
    useEffect(() => {
        if (!jobData?.assistanceRequestId) return;
        const fetchAssistanceData = async () => {
            setAssistanceLoading(true);
            setAssistanceError(null);
            try {
                const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/assistanceRequests`, {
                    headers: { 'auth-token': token },
                    params: { assistanceRequestId: jobData.assistanceRequestId }
                });
                const data = response.data?.ret || response.data;
                if (!data?._id) throw new Error("Dati della richiesta di assistenza non validi");
                setAssistanceData(data);
            } catch (err) {
                console.error("Errore caricamento dati assistenza:", err);
                setAssistanceError("Impossibile caricare i dettagli della richiesta di assistenza.");
            } finally {
                setAssistanceLoading(false);
            }
        };
        fetchAssistanceData();
    }, [jobData, token]);

    // Effetto per calcolare ore totali e riepilogo utenti da appuntamenti
    useEffect(() => {
        if (appointments && appointments.length > 0) {
            let accumulatedTotalCommessaMinutes = 0;
            const usersWorkData = {}; // { userId: { totalMinutes: X, knownLabel: Y, closingStatuses: [] } }

            const appointmentUsersMap = {};
            appointments.forEach(app => {
                if (app.users && Array.isArray(app.users)) {
                    app.users.forEach(u => {
                        if (u.userId && u.userLabel && !appointmentUsersMap[u.userId]) {
                            appointmentUsersMap[u.userId] = u.userLabel;
                        }
                    });
                }
            });

            appointments.forEach(app => {
                if (app.closingDetails && Array.isArray(app.closingDetails)) {
                    app.closingDetails.forEach(detail => {
                        const detailHours = parseInt(detail.workHours, 10) || 0;
                        const detailMinutes = parseInt(detail.workMinutes, 10) || 0;
                        const currentDetailTotalMinutes = (detailHours * 60) + detailMinutes;

                        if (currentDetailTotalMinutes > 0 && detail.userId) {
                            accumulatedTotalCommessaMinutes += currentDetailTotalMinutes;

                            if (!usersWorkData[detail.userId]) {
                                usersWorkData[detail.userId] = { 
                                    totalMinutes: 0, 
                                    knownLabel: appointmentUsersMap[detail.userId] || detail.userLabel || 'Utente sconosciuto',
                                    closingStatuses: []
                                };
                            }
                            usersWorkData[detail.userId].totalMinutes += currentDetailTotalMinutes;
                            // Aggiorna l'etichetta utente se troviamo un valore migliore
                            if (detail.userLabel) {
                                usersWorkData[detail.userId].knownLabel = detail.userLabel;
                            }
                            if (appointmentUsersMap[detail.userId]) {
                                usersWorkData[detail.userId].knownLabel = appointmentUsersMap[detail.userId];
                            }
                            // Raccogli gli stati di chiusura
                            if (detail.closingStatusLabel) {
                                usersWorkData[detail.userId].closingStatuses.push({
                                    label: detail.closingStatusLabel,
                                    date: app.fromDate
                                });
                            }
                        }
                    });
                }
            });

            const commessaH = Math.floor(accumulatedTotalCommessaMinutes / 60);
            const commessaM = accumulatedTotalCommessaMinutes % 60;
            setTotalWorkTime({ hours: commessaH, minutes: commessaM });
            setTotalWorkedMinutes(accumulatedTotalCommessaMinutes);

            const summaryArray = Object.entries(usersWorkData).map(([userId, data]) => {
                const userH = Math.floor(data.totalMinutes / 60);
                const userM = data.totalMinutes % 60;
                // Conta le occorrenze di ogni stato di chiusura
                const statusCounts = {};
                data.closingStatuses.forEach(s => {
                    statusCounts[s.label] = (statusCounts[s.label] || 0) + 1;
                });
                return {
                    userId,
                    userLabel: data.knownLabel,
                    totalHours: userH,
                    totalMinutes: userM,
                    closingStatuses: statusCounts
                };
            }).sort((a, b) => (b.totalHours * 60 + b.totalMinutes) - (a.totalHours * 60 + a.totalMinutes));
            
            setUserWorkSummary(summaryArray);

        } else {
            setUserWorkSummary([]);
            setTotalWorkTime({ hours: 0, minutes: 0 });
        }
    }, [appointments]);

    // Fetch degli appuntamenti collegati
    useEffect(() => {
        if (!jobId || !jobData?.accountId) return; // Attende che jobData e accountId siano disponibili
        const fetchAppointments = async () => {
            setAppointmentsLoading(true);
            setAppointmentsError(null);
            try {
                const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/appointments/byjob`, {
                    headers: { 'auth-token': token },
                    params: { jobId, accountId: jobData.accountId }
                });
                // Recupero gli appuntamenti
                const appointmentsData = response.data.data || response.data || [];
                
                // Ordinamento degli appuntamenti dal più recente al più lontano cronologicamente
                const sortedAppointments = [...appointmentsData].sort((a, b) => {
                    // Per prima cosa controlliamo se abbiamo date valide
                    const dateA = a.fromDate ? new Date(a.fromDate) : null;
                    const dateB = b.fromDate ? new Date(b.fromDate) : null;
                    
                    // Se entrambe le date sono valide, le confrontiamo
                    if (dateA && dateB) {
                        return dateB - dateA; // Ordine decrescente (dal più recente al più lontano)
                    }
                    
                    // Se solo una è valida, quella con la data viene prima
                    if (dateA) return -1;
                    if (dateB) return 1;
                    
                    // Se nessuna è valida, manteniamo l'ordine originale
                    return 0;
                });
                
                setAppointments(sortedAppointments);
            } catch (err) {
                setAppointmentsError("Impossibile caricare gli appuntamenti.");
            } finally {
                setAppointmentsLoading(false);
            }
        };
        fetchAppointments();
    }, [jobId, token, jobData]); // Aggiunto jobData alle dipendenze

    // Gestori di eventi
    const handleEditClick = () => {
        if (jobData?._id) {
            navigate(`/job/edit/${jobData._id}`);
            dispatch(closeModal());
        }
    };
    
    // Gestore del pulsante Pianifica
    const handleScheduleClick = () => {
        navigate(`/jobs/scheduling?jobId=${jobId}`);
        dispatch(closeModal());
    };
    
    // Funzione per gestire la modifica dell'appuntamento
    const handleEditAppointment = async () => {
        if (!appointmentId) return;
        
        // Invece di reindirizzare, apriamo il pannello laterale con l'appuntamento in modalità modifica
        const selectedAppointment = appointments.find(app => app._id === appointmentId);
        
        if (selectedAppointment) {
            // Prepariamo i dati dell'appuntamento prima di utilizzare setSwap
            const appointmentData = JSON.parse(JSON.stringify(selectedAppointment));
            // Impostiamo initialEditMode = true per attivare immediatamente la modale o la modifica
            appointmentData.initialEditMode = true;
            
            // Invece di chiudere e riaprire, usiamo setSwap per cambiare il contenuto del pannello
            // mantenendolo aperto, evitando problemi di sincronizzazione con le animazioni
            dispatch(setSwap({
                mode: 'appointment_view',
                data: appointmentData,
                isOld: true // Mantiene in memoria lo stato precedente
            }));
        } else {
            toast.error('Non è possibile trovare i dati dell\'appuntamento da modificare');
        }
    };
    
    // Funzione per gestire l'eliminazione dell'appuntamento
    const handleDeleteAppointment = async () => {
        if (!appointmentId) return;
        
        // Trova l'appuntamento selezionato
        const selectedApp = appointments.find(app => app._id === appointmentId);
        if (!selectedApp) {
            return;
        }
        
        // Imposta il deleteMode in base alla presenza di groupId
        if (selectedApp.groupId) {
            setDeleteMode('group');
        } else {
            setDeleteMode('single');
        }
        
        setSelectedAppointment(appointmentId);
        setShowDeleteModal(true);
    };
    
    // Funzione per confermare l'eliminazione di un singolo appuntamento
    const confirmDeleteAppointment = async () => {
        try {
            setDeleteInProgress(true);
            
            // Chiamata API per eliminare l'appuntamento
            const baseUrl = getNodeApiUrl();
            
            await axios.delete(`${baseUrl}/appointments/${selectedAppointment}`, {
                headers: { 'Content-Type': 'application/json', 'auth-token': token }
            });
            
            toast.success('Appuntamento eliminato con successo');
            
            // Chiudi il modale di conferma e il pannello laterale
            setShowDeleteModal(false);
            setDeleteInProgress(false);
            dispatch(setHasToRefresh(true)); // Aggiorna la vista del calendario
            dispatch(closeModal());
            
        } catch (error) {
            toast.error('Errore durante l\'eliminazione dell\'appuntamento');
            setDeleteInProgress(false);
        }
    };
    
    // Funzione per confermare l'eliminazione di tutti gli appuntamenti di un gruppo
    const confirmDeleteGroupAppointments = async () => {
        try {
            setDeleteInProgress(true);
            
            // Trova l'appuntamento selezionato per ottenere il groupId
            const selectedApp = appointments.find(app => app._id === selectedAppointment);
            if (!selectedApp || !selectedApp.groupId) {
                toast.error('Impossibile trovare il gruppo di appuntamenti');
                setDeleteInProgress(false);
                return;
            }
            
            // Chiamata API per eliminare tutti gli appuntamenti del gruppo
            const baseUrl = getNodeApiUrl();
            
            await axios.delete(`${baseUrl}/appointments/group/${selectedApp.groupId}`, {
                headers: { 'Content-Type': 'application/json', 'auth-token': token }
            });
            toast.success('Tutti gli appuntamenti del gruppo eliminati con successo');
            
            // Chiudi il modale di conferma e il pannello laterale
            setShowDeleteModal(false);
            setDeleteInProgress(false);
            dispatch(setHasToRefresh(true)); // Aggiorna la vista del calendario
            dispatch(closeModal());
            
        } catch (error) {
            console.error('[DEBUG] Errore durante l\'eliminazione degli appuntamenti del gruppo:', error);
            toast.error('Errore durante l\'eliminazione degli appuntamenti del gruppo');
            setDeleteInProgress(false);
        }
    };

    // Componente per il contenuto del tab Assistenza
    const AssistanceDetailsTabContent = ({ assistanceData, assistanceLoading, assistanceError, palette }) => {
        if (assistanceLoading) return <LoadingSection />;
        if (assistanceError) return <p className="text-red-500">{assistanceError}</p>;
        if (!assistanceData) return <p className="text-gray-500">Nessun dato assistenza disponibile per questa commessa.</p>;

        return (
            <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border" style={{ borderColor: palette.neutralBg || '#edf2f7' }}>
                    <h3 className="text-lg font-semibold mb-3" style={{ color: palette.primary }}>Dettagli Richiesta Assistenza</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <DetailItem label="Codice" value={assistanceData.code || 'N/D'} palette={palette} />
                        <DetailItem label="Cliente" value={assistanceData.customerLabel || 'N/D'} palette={palette} />
                        <DetailItem label="Impianto" value={assistanceData.systemLabel || 'N/D'} palette={palette} />
                        <DetailItem 
                            label="Priorità" 
                            value={
                                (assistanceData.assistanceTypeLabel || 'N/D') + 
                                (assistanceData.assistanceTypeDescription ? ` - ${assistanceData.assistanceTypeDescription}` : '')
                            } 
                            palette={palette} 
                        />
                        <DetailItem label="Pagamento" value={assistanceData.paymentTypeLabel || 'N/D'} palette={palette} />
                        
                        {assistanceData.appointmentTimeSlot && (
                            <DetailItem label="Fascia Oraria" value={assistanceData.appointmentTimeSlot} palette={palette} />
                        )}
                        
                        <DetailItem label="Creazione" 
                            value={assistanceData.creationDate ? moment(assistanceData.creationDate).format('DD/MM/YYYY HH:mm') : 'N/D'} 
                            palette={palette} 
                        />
                        
                        {assistanceData.acceptedDate && 
                            <DetailItem label="Accettazione" 
                                value={moment(assistanceData.acceptedDate).format('DD/MM/YYYY HH:mm')} 
                                palette={palette} 
                            />
                        }
                        
                        {assistanceData.expirationDate && 
                            <DetailItem 
                                label={moment(assistanceData.expirationDate).isBefore(moment()) ? 'Scaduta il' : 'Scadenza'} 
                                value={moment(assistanceData.expirationDate).format('DD/MM/YYYY HH:mm')}
                                palette={palette}
                            />
                        }
                        
                        {assistanceData.closedDate && 
                            <DetailItem label="Chiusura" 
                                value={moment(assistanceData.closedDate).format('DD/MM/YYYY HH:mm')} 
                                palette={palette} 
                            />
                        }
                        
                        <div className="md:col-span-3 mt-2 flex flex-wrap items-center gap-3">
                            <div className="flex items-center">
                                <span className="text-sm font-medium mr-2" style={{ color: palette.text }}>Stato:</span>
                                <span className="px-2 py-1 rounded-md text-sm font-medium" 
                                    style={{ 
                                        backgroundColor: palette.primaryBg || '#ebf5ff', 
                                        color: palette.primaryText || '#3182ce' 
                                    }}>
                                    {assistanceData.statusLabel || 'Stato N/D'}
                                </span>
                            </div>
                            <div className="flex items-center">
                                {assistanceData.warranty ? (
                                    <span className="px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-800">
                                        In Garanzia
                                    </span>
                                ) : (
                                    <span className="px-3 py-1 rounded-md text-sm font-medium bg-red-100 text-red-800">
                                        Fuori Garanzia
                                    </span>
                                )}
                            </div>
                        </div>
                        
                        {(assistanceData.systemTypeLabel || assistanceData.systemAddressLabel) && 
                            <div className="md:col-span-3 p-3 rounded-md" style={{ backgroundColor: palette.neutralLightest || '#f9fafb' }}>
                                <h4 className="text-sm font-medium mb-1" style={{ color: palette.neutralText }}>Impianto:</h4>
                                <div className="flex flex-wrap gap-4">
                                    {assistanceData.systemTypeLabel && 
                                        <p className="text-sm">
                                            <span className="font-medium">Tipo:</span> {assistanceData.systemTypeLabel}
                                        </p>
                                    }
                                    {assistanceData.systemAddressLabel && 
                                        <p className="text-sm">
                                            <span className="font-medium">Indirizzo:</span> {assistanceData.systemAddressLabel}
                                        </p>
                                    }
                                </div>
                            </div>
                        }
                        
                        {/* Descrizione Richiesta */}
                        <div className="md:col-span-3 mt-4 pt-4 border-t" style={{ borderColor: palette.neutralBg || '#edf2f7' }}>
                            <h4 className="text-base font-medium mb-3" style={{ color: palette.text }}>Descrizione Richiesta</h4>
                            
                            {/* Richiesta cliente e note private */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {/* Richiesta cliente */}
                                {assistanceData.customerRequest && (
                                    <div className="bg-gray-50 rounded-md p-3">
                                        <div className="flex items-center mb-2">
                                            <i className="fa-solid fa-quote-left mr-2" style={{ color: palette.primary }}></i>
                                            <h4 className="text-sm font-medium" style={{ color: palette.text }}>Richiesta Cliente</h4>
                                        </div>
                                        <p className="text-sm whitespace-pre-line">{assistanceData.customerRequest}</p>
                                    </div>
                                )}
                                
                                {/* Note private */}
                                {assistanceData.privateNotes && (
                                    <div className="bg-gray-50 rounded-md p-3">
                                        <div className="flex items-center mb-2">
                                            <i className="fa-solid fa-lock mr-2" style={{ color: palette.warningText }}></i>
                                            <h4 className="text-sm font-medium" style={{ color: palette.text }}>Note Private Operatore</h4>
                                        </div>
                                        <p className="text-sm whitespace-pre-line">{assistanceData.privateNotes}</p>
                                    </div>
                                )}
                            </div>
                            
                            {/* Casi d'uso */}
                            {assistanceData.useCases && assistanceData.useCases.length > 0 && (
                                <div>
                                    <div className="flex items-center mb-2">
                                        <i className="fa-solid fa-list-check mr-2" style={{ color: palette.primary }}></i>
                                        <h4 className="text-sm font-medium" style={{ color: palette.text }}>Casi d'uso</h4>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {assistanceData.useCases.map((useCase, index) => {
                                            // Estrai titolo e icona dal caso d'uso
                                            let useCaseTitle = '';
                                            let useCaseIcon = 'fas fa-check';
                                            
                                            if (typeof useCase === 'object') {
                                                useCaseTitle = useCase.title || useCase.useCase || useCase.useCaseLabel || useCase.label || '';
                                                useCaseIcon = useCase.icon || 'fas fa-check';
                                            } else {
                                                useCaseTitle = useCase;
                                            }
                                            
                                            return (
                                                <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-100" 
                                                    style={{ backgroundColor: palette.neutralLightest || '#f9fafb' }}>
                                                    <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" 
                                                        style={{ backgroundColor: `${palette.primary}15`, color: palette.primary }}>
                                                        <i className={useCaseIcon}></i>
                                                    </div>
                                                    <p className="text-sm font-medium" style={{ color: palette.text }}>{useCaseTitle}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Allegati Assistenza */}
                        {assistanceData.uploads && assistanceData.uploads.length > 0 && (
                            <div className="md:col-span-3 mt-4 pt-4 border-t" style={{ borderColor: palette.neutralBg || '#edf2f7' }}>
                                <div className="flex items-center mb-3">
                                    <i className="fa-solid fa-paperclip mr-2" style={{ color: palette.primary }}></i>
                                    <h4 className="text-base font-medium" style={{ color: palette.text }}>
                                        Allegati ({assistanceData.uploads.length})
                                    </h4>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                    {assistanceData.uploads.map((file, index) => {
                                        const fileUrl = file.fileUrl || file.urlFile || file.url || '';
                                        const fileName = file.fileName || file.name || file.title || `Allegato ${index + 1}`;
                                        const fileType = file.fileType || file.kind || file.type || '';
                                        
                                        // Estrai estensione dal nome file o URL
                                        let fileExtension = '';
                                        if (fileName.includes('.')) {
                                            fileExtension = fileName.split('.').pop().toLowerCase();
                                        } else if (fileUrl) {
                                            const urlPath = fileUrl.split('?')[0];
                                            const urlFileName = decodeURIComponent(urlPath.split('/').pop());
                                            if (urlFileName.includes('.')) {
                                                fileExtension = urlFileName.split('.').pop().toLowerCase();
                                            }
                                        }
                                        
                                        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(fileExtension) || 
                                                        fileType.startsWith('image/');
                                        
                                        return (
                                            <div 
                                                key={index}
                                                className="border rounded-lg overflow-hidden bg-white cursor-pointer hover:shadow-md transition-shadow"
                                                onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                                                style={{ borderColor: palette.neutralBg || '#edf2f7' }}
                                            >
                                                <div 
                                                    className="h-24 flex items-center justify-center"
                                                    style={{ backgroundColor: palette.neutralLightest || '#f9fafb' }}
                                                >
                                                    {isImage && fileUrl ? (
                                                        <img 
                                                            src={fileUrl}
                                                            alt={fileName}
                                                            className="h-full w-full object-contain p-1"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div 
                                                        className="flex-col items-center justify-center"
                                                        style={{ display: isImage && fileUrl ? 'none' : 'flex' }}
                                                    >
                                                        <i className={`fa-solid ${
                                                            ['pdf'].includes(fileExtension) ? 'fa-file-pdf text-red-500' :
                                                            ['doc', 'docx'].includes(fileExtension) ? 'fa-file-word text-blue-500' :
                                                            ['xls', 'xlsx'].includes(fileExtension) ? 'fa-file-excel text-green-500' :
                                                            'fa-file text-gray-500'
                                                        } text-3xl mb-1`}></i>
                                                        <span className="text-xs text-gray-400">{fileExtension.toUpperCase() || 'FILE'}</span>
                                                    </div>
                                                </div>
                                                <div className="p-2 border-t" style={{ borderColor: palette.neutralBg || '#edf2f7' }}>
                                                    <p className="text-xs font-medium truncate" title={fileName}>
                                                        {fileName}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Componente per il contenuto del tab Mappa
    const MapTabContent = ({ jobData, accountData, palette }) => {
        // Verifichiamo la disponibilità delle coordinate
        if (!jobData?.geoData?.coordinates || jobData.geoData.coordinates.length !== 2) {
            return <p className="text-sm" style={{ color: palette.textSubtle }}>Coordinate dell'impianto non disponibili.</p>;
        }
        
        // Verifichiamo la disponibilità delle coordinate della sede
        const headquarterCoordinates = accountData?.settings?.headquarterCoordinates;
        const hasHeadquarterCoords = headquarterCoordinates && Array.isArray(headquarterCoordinates) && headquarterCoordinates.length === 2;
        
        // Formattazione coordinate per Google Maps
        // headquarterCoordinates è [latitudine, longitudine]
        // geoData.coordinates è [longitudine, latitudine]
        let originCoords = "";
        let destinationCoords = "";
        
        if (hasHeadquarterCoords) {
            // Formato [lat, lng] -> "lat,lng" per Google Maps
            originCoords = `${headquarterCoordinates[0]},${headquarterCoordinates[1]}`;
        }
        
        // Formato [lng, lat] -> "lat,lng" per Google Maps
        destinationCoords = `${jobData.geoData.coordinates[1]},${jobData.geoData.coordinates[0]}`;
        
        // URL per mappa o direzioni
        let mapUrl;
        if (hasHeadquarterCoords) {
            // URL per direzioni se abbiamo entrambe le coordinate
            mapUrl = `https://www.google.com/maps/embed/v1/directions?key=${import.meta.env.VITE_GOOGLE_MAPS_KEY}&origin=${originCoords}&destination=${destinationCoords}&mode=driving`;
        } else {
            // URL per singola posizione se non abbiamo le coordinate della sede
            mapUrl = `https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_KEY}&q=${destinationCoords}`;
        }
        
        // URL per link esterno
        const externalUrl = hasHeadquarterCoords
            ? `https://www.google.com/maps/dir/?api=1&origin=${originCoords}&destination=${destinationCoords}&travelmode=driving`
            : `https://www.google.com/maps/search/?api=1&query=${destinationCoords}`;
        
        return (
            <div className="p-4">
                {!hasHeadquarterCoords && (
                    <div 
                        className="mb-4 p-3 rounded-lg" 
                        style={{ backgroundColor: palette.warningBg || '#fff3cd', color: palette.warningText || '#856404' }}
                    >
                        <p className="text-sm">
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            Le coordinate della sede aziendale non sono configurate. Viene mostrata solo la posizione dell'impianto.
                        </p>
                    </div>
                )}
                
                <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                    <div>
                        <h3 className="text-lg font-semibold mb-1" style={{ color: palette.text }}>
                            {hasHeadquarterCoords ? "Percorso" : "Posizione Impianto"}
                        </h3>
                        <p className="text-sm" style={{ color: palette.neutralText }}>
                            {hasHeadquarterCoords 
                                ? "Percorso dalla sede aziendale all'impianto"
                                : `Posizione dell'impianto ${jobData.systemLabel || ''}`
                            }
                        </p>
                    </div>
                    
                    <div className="mt-2 sm:mt-0">
                        <a 
                            href={externalUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="px-4 py-2 rounded-md text-sm flex items-center" 
                            style={{ backgroundColor: palette.primary, color: palette.textOnPrimary }}
                        >
                            <i className="fas fa-external-link-alt mr-2"></i>
                            Apri in Google Maps
                        </a>
                    </div>
                </div>
                
                {/* Informazioni di distanza e tempo (se disponibili) */}
                {(jobData.distanceKm || jobData.distanceTime) && (
                    <div className="mb-4 grid grid-cols-2 gap-4">
                        {jobData.distanceKm && (
                            <div className="text-center p-3 rounded-lg" style={{ backgroundColor: palette.neutralLightest || '#f8f9fa' }}>
                                <p className="text-xs" style={{ color: palette.neutralText }}>Distanza</p>
                                <p className="text-sm font-medium" style={{ color: palette.text }}>
                                    {typeof jobData.distanceKm === 'number' ? 
                                      `${jobData.distanceKm.toFixed(1)} km` : 
                                      `${parseInt(jobData.distanceKm) || 0} km`}
                                </p>
                            </div>
                        )}
                        
                        {jobData.distanceTime && (
                            <div className="text-center p-3 rounded-lg" style={{ backgroundColor: palette.neutralLightest || '#f8f9fa' }}>
                                <p className="text-xs" style={{ color: palette.neutralText }}>Tempo</p>
                                <p className="text-sm font-medium" style={{ color: palette.text }}>
                                    {(() => {
                                        const mins = parseInt(jobData.distanceTime);
                                        const hours = Math.floor(mins / 60);
                                        const remainingMins = mins % 60;
                                        return `${hours.toString().padStart(1, '0')}:${remainingMins.toString().padStart(2, '0')}`;
                                    })()}
                                </p>
                            </div>
                        )}
                    </div>
                )}
                
                <div className="rounded-lg overflow-hidden border" style={{ height: '400px', borderColor: palette.neutralBg }}>
                    <iframe 
                        title="Google Maps Percorso"
                        width="100%"
                        height="100%"
                        frameBorder="0" 
                        style={{ border: 0 }} 
                        src={mapUrl}
                        allowFullScreen>
                    </iframe>
                </div>
            </div>
        );
    };
    
    // Componente per il contenuto del tab Impianto
    const SystemDetailsTabContent = ({ jobData, systemData, systemLoading, systemError, palette }) => {
        if (systemLoading && !jobData.systemLabel) return <LoadingSection />;
        if (systemError && !jobData.systemLabel) return <p className="text-red-500">Errore caricamento dettagli impianto.</p>;
        if (!jobData.systemLabel && !systemData && !systemLoading && !systemError) return <p className="text-gray-500">Nessun dato impianto disponibile per questa commessa.</p>;

        return (
            <div className="space-y-3">
                {/* Nome e Indirizzo da jobData (prioritari) o systemData come fallback */}
                <DetailItem label="Nome Impianto" value={jobData.systemLabel || (systemData ? systemData.name : 'N/D')} palette={palette} />
                <DetailItem label="Indirizzo Impianto" value={jobData.systemAddressLabel || (systemData ? systemData.address?.formattedAddress : 'N/D')} palette={palette} />
                
                {/* Distanza e Tempo di Percorrenza */}
                {(jobData.distanceKm || jobData.distanceTime) && (
                    <div className="mt-3 space-y-2 p-3 rounded-md" style={{backgroundColor: palette.neutralLightest || '#f9fafb'}}>
                        <div className="flex items-center mb-1">
                            <DynamicIcon iconClass="fa-solid fa-location-dot" style={{color: palette.primary, marginRight: '8px'}} />
                            <h4 className="text-sm font-medium" style={{ color: palette.text }}>Informazioni di Distanza</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {jobData.distanceKm && (
                                <div className="flex items-center">
                                    <DynamicIcon iconClass="fa-solid fa-route" style={{color: palette.neutral, marginRight: '8px'}} />
                                    <div>
                                        <span className="text-xs block" style={{color: palette.neutralText}}>Distanza</span>
                                        <span className="font-medium" style={{color: palette.text}}>{jobData.distanceKm.toFixed(1)} km</span>
                                    </div>
                                </div>
                            )}
                            {jobData.distanceTime && (
                                <div className="flex items-center">
                                    <DynamicIcon iconClass="fa-solid fa-clock" style={{color: palette.neutral, marginRight: '8px'}} />
                                    <div>
                                        <span className="text-xs block" style={{color: palette.neutralText}}>Tempo di Percorrenza</span>
                                        <span className="font-medium" style={{color: palette.text}}>
                                            {Math.floor(jobData.distanceTime)} min
                                            {(jobData.distanceTime % 1) > 0 && ` ${Math.round((jobData.distanceTime % 1) * 60)} sec`}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Tipi Impianto Associati - da jobData o systemData */}
                {(() => {
                    // Determiniamo quali dati di systemTypeList mostrare
                    let typeListToShow = [];
                    
                    // Priorità a jobData.systemTypeList se disponibile
                    if (jobData.systemTypeList && jobData.systemTypeList.length > 0) {
                        typeListToShow = jobData.systemTypeList;
                    }
                    // Altrimenti controlliamo se ci sono dati in systemData
                    else if (systemData && systemData.typeList && systemData.typeList.length > 0) {
                        typeListToShow = systemData.typeList;
                    }
                    
                    if (typeListToShow.length > 0) {
                        return (
                            <div className="mt-2">
                                <p className="text-sm font-medium mb-1" style={{ color: palette.neutral }}>Tipi Impianto</p>
                                <div className="space-y-2">
                                    {typeListToShow.map((typeItem, index) => {
                                        const isWarrantyValid = typeItem.expirationWarrantyDate && moment(typeItem.expirationWarrantyDate).isAfter(moment());
                                        const warrantyBadgeText = typeItem.expirationWarrantyDate ? (isWarrantyValid ? "In Garanzia" : "Garanzia Scaduta") : "Garanzia N/D";
                                        const warrantyBadgeStyle = typeItem.expirationWarrantyDate ? 
                                            (isWarrantyValid ? { backgroundColor: palette.successBg, color: palette.successText } : { backgroundColor: palette.dangerBg, color: palette.dangerText }) : 
                                            { backgroundColor: palette.neutralBg, color: palette.neutralText };

                                        return (
                                            <div key={index} className="p-3 rounded-md" style={{backgroundColor: palette.secondaryBg || '#f9fafb', border: `1px solid ${palette.neutralBg || '#e5e7eb'}`}}>
                                                <p className="text-md font-semibold" style={{color: palette.text || palette.neutralText}}>{typeItem.typeLabel}</p>
                                                <p className="text-xs" style={{color: palette.neutral}}>Codice: {typeItem.typeCode || typeItem.code || "N/D"}</p>
                                                <p className="text-xs" style={{color: palette.neutral}}>Collaudo: {typeItem.testedDate ? moment(typeItem.testedDate).format("DD/MM/YYYY") : "N/D"}</p>
                                                <div className="flex items-center justify-between mt-1">
                                                    <p className="text-xs" style={{color: palette.neutral}}>Scad. Garanzia: {typeItem.expirationWarrantyDate ? moment(typeItem.expirationWarrantyDate).format("DD/MM/YYYY") : "N/D"}</p>
                                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={warrantyBadgeStyle}>
                                                        {warrantyBadgeText}
                                                    </span>
                                                </div>
                                                {typeItem.notes && (
                                                    <p className="text-xs mt-1 italic" style={{color: palette.neutral}}>Note: {typeItem.notes}</p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    }
                    
                    return null;
                })()}
                

                {/* Descrizione e Note da systemData (se disponibili e non già coperti) */}
                {systemData && (
                    <>
                        {systemData.typeLabel && (!jobData.systemTypeList || jobData.systemTypeList.length === 0) && <DetailItem label="Tipo Generico Impianto (da Sistema)" value={systemData.typeLabel} palette={palette} />}
                        {systemData.description && <DetailItem label="Descrizione Aggiuntiva (da Sistema)" value={systemData.description} palette={palette} isExpandable={true} />}
                        {systemData.notes && <DetailItem label="Note Aggiuntive (da Sistema)" value={systemData.notes} palette={palette} isExpandable={true} />}
                    </>
                )}
            </div>
        );
    };

    // Early returns per stati di caricamento ed errore
    if (loading) return <LoadingSection />;
    if (error) return <div className="p-4 text-red-500">{error}</div>;
    if (!jobData) return <div className="p-4">Nessun dato trovato per la commessa.</div>;

    // Calcolo allegati appuntamenti per il contatore
    let appointmentAttachmentsCount = 0;
    if (appointments && appointments.length > 0) {
        appointments.forEach(appointment => {
            if (appointment.closingDetails && Array.isArray(appointment.closingDetails)) {
                appointment.closingDetails.forEach(detail => {
                    if (detail.files && Array.isArray(detail.files)) {
                        appointmentAttachmentsCount += detail.files.length;
                    }
                });
            }
        });
    }
    
    // Conteggio totale allegati (commessa + appuntamenti)
    const jobUploadsCount = Array.isArray(jobData?.uploads) ? jobData.uploads.length : 0;
    const totalAttachmentsCount = jobUploadsCount + appointmentAttachmentsCount;

    // Conteggio materiali
    const materialsCount = Array.isArray(jobData?.materialsList) ? jobData.materialsList.length : 0;

    // Titoli tab con conteggio elementi
    const tabTitles = [
        `PIANIFICAZIONE (${Array.isArray(appointments) ? appointments.length : 0})`,
        `MATERIALI (${materialsCount})`,
        `IMPIANTO ${jobData.systemLabel || systemData ? '(1)' : '(0)'}`,
        `ALLEGATI (${totalAttachmentsCount})`
    ];
    
    // Aggiungo il tab Assistenza se la commessa è collegata ad una richiesta
    if (jobData?.assistanceRequestId) {
        tabTitles.splice(4, 0, `ASSISTENZA ${assistanceData ? '(1)' : '(0)'}`); // Inserisco dopo gli allegati
    }
    
    // Aggiungo sempre il tab Mappa se abbiamo le coordinate dell'impianto
    if (jobData?.geoData?.coordinates && jobData.geoData.coordinates.length === 2) {
        tabTitles.push('MAPPA');
    }

    // Contenuto dei tab
    let tabContents = [
        // Contenuto Tab PIANIFICAZIONE
        <div key="tab-pianificazione">
            {appointmentsLoading && <LoadingSection />}
            {appointmentsError && <p className="text-red-500">{appointmentsError}</p>}
            {!appointmentsLoading && !appointmentsError && appointments.length > 0 ? (
                <div className="space-y-4">
                    {appointments.map((appointment) => (
                        <AppointmentCard
                            key={appointment._id}
                            appointment={appointment}
                            token={token}
                            apiBaseUrl={import.meta.env.VITE_API_BASE_URL || ''}
                            selectedAppointmentId={appointmentId}
                        />
                    ))}
                </div>
            ) : (
                <p className="text-sm" style={{ color: palette.textSubtle }}>Nessun appuntamento pianificato.</p>
            )}
        </div>,

        // Contenuto Tab MATERIALI
        <div key="tab-materiali">
            {jobData?._id && (
                <JobMaterialsPanel 
                    jobId={jobData._id}
                    jobCode={jobData.code}
                    customerLabel={jobData.customerLabel}
                    onRefresh={() => {}}
                />
            )}
        </div>,

        // Contenuto Tab Impianto
        <SystemDetailsTabContent key="tab-impianto" jobData={jobData} systemData={systemData} systemLoading={systemLoading} systemError={systemError} palette={palette} />,

        // Contenuto Tab ALLEGATI
        <div key="tab-allegati">
            {(() => {
                const uploads = jobData?.uploads;
                
                // Raccolta allegati da tutti gli appuntamenti (closingDetails)
                const appointmentAttachments = [];
                if (appointments && appointments.length > 0) {
                    appointments.forEach(appointment => {
                        if (appointment.closingDetails && Array.isArray(appointment.closingDetails)) {
                            appointment.closingDetails.forEach(detail => {
                                if (detail.files && Array.isArray(detail.files) && detail.files.length > 0) {
                                    detail.files.forEach(file => {
                                        // Aggiungo info sul file con metadata appuntamento e utente
                                        appointmentAttachments.push({
                                            // Il file può essere un oggetto o una stringa
                                            ...(typeof file === 'object' ? file : { fileUrl: file, urlFile: file }),
                                            // Metadata aggiuntivi per contestualizzare l'allegato
                                            _appointmentDate: appointment.fromDate,
                                            _appointmentId: appointment._id,
                                            _userName: detail.userLabel,
                                            _userId: detail.userId
                                        });
                                    });
                                }
                            });
                        }
                    });
                }
                
                // Funzione per determinare se il file è un'immagine
                const isImageFile = (file) => {
                    if (!file) return false;
                    
                    // Se è un oggetto con URL
                    if (typeof file === 'object' && file.url) {
                        const url = file.url.toLowerCase();
                        // Estrai l'estensione dall'URL
                        const urlParts = url.split('/');
                        const fileName = urlParts[urlParts.length - 1].split('?')[0];
                        const ext = fileName.split('.').pop().toLowerCase();
                        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
                    }
                    
                    // Se è un oggetto con proprietà fileName/name/path
                    if (typeof file === 'object') {
                        const fileName = file.fileName || file.name || file.path || '';
                        const ext = fileName.split('.').pop().toLowerCase();
                        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
                    }
                    
                    // Se è una stringa
                    if (typeof file === 'string') {
                        const parts = file.split('.');
                        const ext = parts.length > 1 ? parts.pop().toLowerCase() : '';
                        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
                    }
                    
                    return false;
                };
                
                // Funzione per ottenere l'icona in base al tipo di file
                const getFileTypeIcon = (file) => {
                    if (!file) return 'fa-solid fa-file';
                    
                    let ext = '';
                    
                    // Se è un oggetto con URL
                    if (typeof file === 'object' && file.url) {
                        const url = file.url.toLowerCase();
                        // Estrai l'estensione dall'URL
                        const urlParts = url.split('/');
                        const fileName = urlParts[urlParts.length - 1].split('?')[0];
                        ext = fileName.split('.').pop().toLowerCase();
                        
                        // Per i file di Google Cloud Storage
                        if (url.includes('storage.googleapis.com')) {
                            if (url.includes('contentType=application/pdf') || url.endsWith('.pdf')) {
                                ext = 'pdf';
                            }
                        }
                    } 
                    // Se è un oggetto con proprietà fileName/name/path
                    else if (typeof file === 'object') {
                        const fileName = file.fileName || file.name || file.path || '';
                        ext = fileName.split('.').pop().toLowerCase();
                    } 
                    // Se è una stringa
                    else if (typeof file === 'string') {
                        const parts = file.split('.');
                        ext = parts.length > 1 ? parts.pop().toLowerCase() : '';
                    }
                    
                    const iconMap = {
                        // Documenti
                        'pdf': 'fa-solid fa-file-pdf',
                        'doc': 'fa-solid fa-file-word',
                        'docx': 'fa-solid fa-file-word',
                        'xls': 'fa-solid fa-file-excel',
                        'xlsx': 'fa-solid fa-file-excel',
                        'ppt': 'fa-solid fa-file-powerpoint',
                        'pptx': 'fa-solid fa-file-powerpoint',
                        'txt': 'fa-solid fa-file-lines',
                        // Immagini
                        'jpg': 'fa-solid fa-file-image',
                        'jpeg': 'fa-solid fa-file-image',
                        'png': 'fa-solid fa-file-image',
                        'gif': 'fa-solid fa-file-image',
                        'bmp': 'fa-solid fa-file-image',
                        'svg': 'fa-solid fa-file-image',
                        'webp': 'fa-solid fa-file-image',
                        // Archivi
                        'zip': 'fa-solid fa-file-zipper',
                        'rar': 'fa-solid fa-file-zipper',
                        '7z': 'fa-solid fa-file-zipper',
                        'tar': 'fa-solid fa-file-zipper',
                        'gz': 'fa-solid fa-file-zipper',
                        // Audio/Video
                        'mp3': 'fa-solid fa-file-audio',
                        'wav': 'fa-solid fa-file-audio',
                        'mp4': 'fa-solid fa-file-video',
                        'avi': 'fa-solid fa-file-video',
                        'mov': 'fa-solid fa-file-video',
                        'wmv': 'fa-solid fa-file-video',
                    };
                    
                    return iconMap[ext] || 'fa-solid fa-file';
                };
                
                // Funzione per ottenere l'URL dell'immagine
                const getImageUrl = (file) => {
                    // Se è un oggetto con proprietà url
                    if (typeof file === 'object' && file.url) {
                        return file.url;
                    }
                    
                    // Se è già presente un URL completo, lo usiamo direttamente
                    if (typeof file === 'object' && file.fileUrl) {
                        return file.fileUrl;
                    }
                    
                    // Se è un oggetto con altre proprietà
                    if (typeof file === 'object') {
                        return `${import.meta.env.VITE_API_BASE_URL || ''}/uploads/${file.path || file.filename || file.fileName || file.name}`;
                    }
                    
                    // Se è una stringa
                    if (typeof file === 'string') {
                        if (file.startsWith('http')) {
                            return file;
                        }
                        return `${import.meta.env.VITE_API_BASE_URL || ''}/uploads/${file}`;
                    }
                    
                    return '';
                };
                
                // Funzione per aprire il file in una nuova tab
                const openFileInNewTab = (file) => {
                    // Se è un oggetto con proprietà url
                    if (typeof file === 'object' && file.url) {
                        window.open(file.url, '_blank');
                        return;
                    }
                    
                    // Se è disponibile l'URL completo lo usiamo direttamente
                    if (typeof file === 'object' && file.fileUrl) {
                        window.open(file.fileUrl, '_blank');
                        return;
                    }
                    
                    // Se è un oggetto, proviamo a costruire l'URL
                    if (typeof file === 'object') {
                        const filePath = file.path || file.filename || file.fileName || file.name;
                        if (!filePath) {
                            console.error('File senza percorso valido:', file);
                            return;
                        }
                        const url = `${import.meta.env.VITE_API_BASE_URL || ''}/uploads/${filePath}`;
                        window.open(url, '_blank');
                        return;
                    }
                    
                    // Se è una stringa
                    if (typeof file === 'string') {
                        if (file.startsWith('http')) {
                            window.open(file, '_blank');
                        } else {
                            const url = `${import.meta.env.VITE_API_BASE_URL || ''}/uploads/${file}`;
                            window.open(url, '_blank');
                        }
                        return;
                    }
                    
                    console.error('Formato file non valido:', file);
                };
                
                // Funzione per scaricare tutti i file
                const downloadAllFiles = async () => {
                    setDownloadingAllFiles(true);
                    try {
                        // Assicuriamoci che uploads sia un array
                        if (!Array.isArray(uploads) || uploads.length === 0) {
                            console.error('Nessun file disponibile per il download');
                            return;
                        }
                        
                        // Apri ogni file in una nuova scheda
                        for (let i = 0; i < uploads.length; i++) {
                            const file = uploads[i];
                            
                            // Utilizziamo sempre fileUrl se disponibile
                            if (file.fileUrl) {
                                setTimeout(() => {
                                    window.open(file.fileUrl, '_blank');
                                }, i * 300); // Aggiungiamo un ritardo per evitare il blocco dei popup
                            } else {
                                // Fallback con percorso costruito
                                const filePath = file.path || file.filename || file.fileName || file.name;
                                if (filePath) {
                                    const url = `${import.meta.env.VITE_API_BASE_URL || ''}/uploads/${filePath}`;
                                    setTimeout(() => {
                                        window.open(url, '_blank');
                                    }, i * 300); // Ritardo progressivo
                                } else {
                                    console.error('File senza percorso valido:', file);
                                }
                            }
                        }
                    } catch (err) {
                        console.error('Errore nel download dei file:', err);
                    } finally {
                        // Aspetta un po' prima di rimuovere lo stato di download
                        setTimeout(() => {
                            setDownloadingAllFiles(false);
                        }, uploads?.length ? uploads.length * 300 + 500 : 1000);
                    }
                };
                

                // Controllo se ci sono allegati commessa o appuntamenti
                const hasJobAttachments = Array.isArray(uploads) && uploads.length > 0;
                const hasAppointmentAttachments = appointmentAttachments.length > 0;
                
                if (!hasJobAttachments && !hasAppointmentAttachments) {
                    return (
                        <div className="mt-2 p-4 text-center">
                            <div className="text-gray-500 italic">
                                Nessun allegato presente
                            </div>
                        </div>
                    );
                }
                
                return (
                    <div className="space-y-6">
                        {/* Allegati Commessa */}
                        {hasJobAttachments && (
                            <div>
                                <h4 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: palette.primary }}>
                                    <DynamicIcon iconClass="fa-solid fa-paperclip" />
                                    Allegati Commessa
                                </h4>
                                <AttachmentsViewer 
                                    attachments={uploads} 
                                    title={`Allegati (${uploads.length})`}
                                    showPreview={true}
                                    downloadAllHandler={downloadAllFiles}
                                    downloadInProgress={downloadingAllFiles}
                                />
                            </div>
                        )}
                        
                        {/* Allegati Appuntamenti */}
                        {hasAppointmentAttachments && (
                            <div>
                                <h4 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: palette.primary }}>
                                    <DynamicIcon iconClass="fa-solid fa-calendar-check" />
                                    Allegati Appuntamenti ({appointmentAttachments.length})
                                </h4>
                                <div className="text-sm text-gray-600 mb-3 italic">
                                    Allegati caricati durante la chiusura degli appuntamenti
                                </div>
                                <AttachmentsViewer 
                                    attachments={appointmentAttachments} 
                                    title={`File caricati (${appointmentAttachments.length})`}
                                    showPreview={true}
                                    lazyLoad={true}
                                />
                            </div>
                        )}
                    </div>
                );
            
            })()}
        </div>
    ];

    // Aggiungo il contenuto del tab Assistenza se la commessa è collegata ad una richiesta di assistenza
    if (jobData?.assistanceRequestId) {
        tabContents.splice(4, 0, 
            <AssistanceDetailsTabContent 
                key="tab-assistenza" 
                assistanceData={assistanceData} 
                assistanceLoading={assistanceLoading} 
                assistanceError={assistanceError} 
                palette={palette} 
            />
        );
    }
    
    // Aggiungo contenuto per il tab Mappa se abbiamo le coordinate
    if (jobData?.geoData?.coordinates && jobData.geoData.coordinates.length === 2) {
        tabContents.push(
            <MapTabContent 
                key="tab-mappa"
                jobData={jobData} 
                accountData={accountData} 
                palette={palette}
            />
        );
    }

    return (
        <>  
            {/* Modale di conferma per eliminazione appuntamento */}
            {showDeleteModal && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000000]" data-testid="delete-modal">
                    <div className="absolute inset-0 bg-black opacity-50"></div>
                    <div className="bg-white p-6 rounded-lg shadow-xl z-10 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4 text-red-600">
                            {deleteMode === 'single' ? 'Elimina appuntamento' : 'Elimina appuntamenti'}
                        </h3>
                        
                        {deleteMode === 'single' ? (
                            <p className="mb-6">
                                Sei sicuro di voler eliminare questo appuntamento? <strong>Questa azione non può essere annullata.</strong>
                            </p>
                        ) : (
                            <div className="mb-6">
                                <p className="mb-4">
                                    Questo appuntamento fa parte di una <strong>serie ricorrente</strong>.
                                </p>
                                <p className="mb-4">
                                    Puoi scegliere di eliminare solo questo specifico appuntamento oppure 
                                    <strong> tutti gli appuntamenti</strong> della serie.
                                </p>
                                <p>
                                    <strong>Attenzione:</strong> Questa azione non può essere annullata.
                                </p>
                            </div>
                        )}
                        
                        <div className={`flex ${deleteMode === 'group' ? 'flex-col' : 'justify-end'} gap-3`}>
                            {/* Pulsante Annulla sempre presente */}
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleteInProgress}
                                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors duration-200"
                            >
                                Annulla
                            </button>
                            
                            {deleteMode === 'single' ? (
                                <button
                                    onClick={confirmDeleteAppointment}
                                    disabled={deleteInProgress}
                                    className="px-4 py-2 text-white rounded-md transition-colors duration-200"
                                    style={{ backgroundColor: palette.error || '#dc3545' }}
                                >
                                    {deleteInProgress ? 'Eliminazione in corso...' : 'Elimina'}
                                </button>
                            ) : (
                                <div className="flex flex-col gap-2 w-full mt-2">
                                    <button
                                        onClick={confirmDeleteAppointment}
                                        disabled={deleteInProgress}
                                        className="px-4 py-2 text-white bg-orange-500 rounded-md hover:bg-orange-600 transition-colors duration-200 w-full"
                                    >
                                        {deleteInProgress ? 'Eliminazione in corso...' : 'Elimina solo questo appuntamento'}
                                    </button>
                                    <button
                                        onClick={confirmDeleteGroupAppointments}
                                        disabled={deleteInProgress}
                                        className="px-4 py-2 text-white rounded-md transition-colors duration-200 w-full"
                                        style={{ backgroundColor: palette.error || '#dc3545' }}
                                    >
                                        {deleteInProgress ? 'Eliminazione in corso...' : 'Elimina tutti gli appuntamenti della serie'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div className="p-4 sm:p-6 md:p-8 flex flex-col xl:flex-row gap-6 bg-gray-50 min-h-full">
                {/* Colonna Sinistra - Dettagli Commessa */}
                <div className="bg-white shadow-xl rounded-lg overflow-hidden flex flex-col gap-y-6 w-full xl:w-1/3 h-fit" style={{ borderTop: `4px solid ${palette.primary}` }}>
                    <div style={{ backgroundColor: palette.tableHeaderBg, borderBottom: '1px solid #eee' }} className="p-4">
                        <h3 className="text-lg font-bold flex items-center" style={{ color: palette.tableHeaderText }}>
                            <DynamicIcon iconClass="fa-solid fa-briefcase" /> <span className="ml-5">Dettagli Commessa</span>
                        </h3>
                        {jobData?._id && (
                            <p className="text-xs font-light mt-1" style={{ color: palette.tableHeaderText, opacity: 0.8 }}>
                                ID: {jobData._id}
                            </p>
                        )}
                    </div>
                    <div className="px-6 pt-4 space-y-4">
                        <div>
                            <p className="text-xs font-medium" style={{ color: palette.neutralText }}>Codice Commessa</p>
                            <div className="flex items-center justify-between mt-1">
                                <div>
                                    <p className="text-lg font-bold" style={{ color: palette.text }}>{jobData.code || "N/D"}</p>
                                    {jobData.oldSWCode && (
                                        <p className="text-xs italic" style={{ color: palette.primary }} title="Codice vecchio software">
                                            ({jobData.oldSWCode})
                                        </p>
                                    )}
                                </div>
                                {jobData.statusId && (() => {
                                    const statusInfo = getJobStatusInfo(jobData.statusId);
                                    return (
                                        <span style={{
                                            backgroundColor: statusInfo.color + '33', // Aggiungo trasparenza
                                            color: statusInfo.color,
                                            padding: '3px 10px',
                                            fontSize: '0.7rem',
                                            fontWeight: '600',
                                            borderRadius: '9999px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            display: 'inline-block'
                                        }}>
                                            {statusInfo.label}
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                            <DetailItem label="Tipo Commessa" value={jobData.jobTypeLabel} palette={palette} />
                            <DetailItem label="Cliente" value={jobData.customerLabel} palette={palette} />
                            {jobData.assistanceRequestId && <DetailItem label="Rif. Richiesta Assistenza" value={jobData.assistanceRequestCode} palette={palette} />}
                            {(jobData.jobTypeMaintenance === true || (jobData.jobType && jobData.jobType.maintenance === true)) && jobData.maintenanceCode && 
                                <DetailItem label="Codice Manutenzione" value={jobData.maintenanceCode} palette={palette} />}
                            
                            {/* Visualizzazione delle azioni di manutenzione */}
                            {jobData?.maintenanceActions && jobData.maintenanceActions.length > 0 && (
                                <div className="md:col-span-2">
                                    <p className="text-xs font-medium mb-2" style={{ color: palette.textOnBackground, opacity: 0.7 }}>Azioni di Manutenzione</p>
                                    <div className="mt-1 space-y-2 border rounded-md p-2" style={{ borderColor: palette.neutralBg }}>
                                        {jobData.maintenanceActions.map((action, index) => (
                                            <div key={action.actionId || index} className="border-b last:border-b-0 pb-2 last:pb-0" style={{ borderColor: palette.neutralLighter }}>
                                                <div className="flex justify-between items-center">
                                                    <p className="text-sm font-semibold" style={{ color: palette.text }}>{action.title}</p>
                                                    <span className="text-xs px-2 py-1 rounded-full" 
                                                        style={{
                                                            backgroundColor: action.statusLabel?.toLowerCase().includes('pianificare') ? palette.warningBg : palette.primaryBg,
                                                            color: action.statusLabel?.toLowerCase().includes('pianificare') ? palette.warningText : palette.primaryText
                                                        }}>
                                                        {action.statusLabel || 'N/D'}
                                                    </span>
                                                </div>
                                                {action.notes && <p className="text-xs mt-1" style={{ color: palette.textOnBackground }}>{action.notes}</p>}
                                                <div className="flex justify-between text-xs mt-1" style={{ color: palette.neutralText }}>
                                                    <span>Mese: {action.month ? moment().month(action.month - 1).format('MMMM') : 'N/D'}</span>
                                                    <span>Anno: {action.year || 'N/D'}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* Informazioni sull'impianto in formato compatto */}
                            <div className="md:col-span-2 border-t pt-3 mt-2" style={{ borderColor: palette.neutralBg }}>
                                <p className="text-xs font-medium mb-2" style={{ color: palette.textOnBackground, opacity: 0.7 }}>Informazioni Impianto</p>
                                <div className="space-y-1 pb-3 border-b mb-3" style={{ borderColor: palette.neutralBg }}>
                                    
                                    <p className="font-bold" style={{ color: palette.text }}>
                                        {jobData.systemLabel || "Impianto non specificato"}
                                    </p>
                                    <p className="text-sm" style={{ color: palette.text }}>
                                        {jobData.systemAddressLabel || "Indirizzo non specificato"}
                                        {' '}
                                        <span className="italic text-sm font-medium" style={{ color: palette.neutralText }}>
                                            ({
                                                (() => {
                                                    const distanceKm = jobData.geoData?.distanceKm || jobData.distanceKm || 0;
                                                    return parseInt(distanceKm) || 0;
                                                })()
                                            } km, {
                                                (() => {
                                                    const distanceTime = jobData.geoData?.distanceTime || jobData.distanceTime || 0;
                                                    const mins = parseInt(distanceTime);
                                                    const hours = Math.floor(mins / 60);
                                                    const remainingMins = mins % 60;
                                                    return `${hours}:${remainingMins.toString().padStart(2, '0')}`;
                                                })()
                                            })
                                        </span>
                                    </p>
                                    
                                    {/* Badge per i tipi di impianto */}
                                    {jobData.systemTypeList && jobData.systemTypeList.length > 0 && (
                                        <div className="mt-1 flex flex-wrap gap-1">
                                            {jobData.systemTypeList.map((type, index) => (
                                                <span 
                                                    key={`system-type-${index}`} 
                                                    className="inline-block text-xs px-2 py-1 rounded-full" 
                                                    style={{ 
                                                        backgroundColor: palette.primary, 
                                                        color: palette.textOnPrimary || '#ffffff',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                    }}
                                                >
                                                    {type.typeLabel}
                                                    {type.expirationWarrantyDate && (
                                                        <span className="ml-1 text-xs opacity-90">
                                                            (scad. {moment(type.expirationWarrantyDate).format("MM/YY")})
                                                        </span>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <DetailItem label="Numero Addetti Previsti" value={jobData.employeesNumber?.toString()} palette={palette} />
                            <DetailItem 
                                label="Ore Previste" 
                                value={jobData.expectedHours?.toString()} 
                                palette={palette} 
                                totalWorkedMinutes={totalWorkedMinutes} 
                            />
                            <DetailItem label="Data Scadenza Prevista" value={jobData.dueDate ? moment(jobData.dueDate).format("DD/MM/YYYY") : "N/D"} palette={palette} />
                            {jobData.creationDate && <DetailItem label="Data Creazione" value={jobData.creationDate ? moment(jobData.creationDate).format("DD/MM/YYYY HH:mm") : "N/D"} palette={palette} />}
                            {jobData.lastUpdateDate && <DetailItem label="Ultimo Aggiornamento" value={jobData.lastUpdateDate ? moment(jobData.lastUpdateDate).format("DD/MM/YYYY HH:mm") : "N/D"} palette={palette} />}
                            
                            {/* Note Commessa con show/hide */}
                            {(jobData.notes || jobData.description) && (() => {
                                // Usa notes se presente, altrimenti description
                                const notesContent = jobData.notes || jobData.description;
                                return (
                                    <div className="md:col-span-2 py-2">
                                        <p className="text-sm font-medium" style={{ color: palette.textOnBackground, opacity: 0.7 }}>Note</p>
                                        <div className="text-base font-semibold mt-1" style={{ color: palette.textOnBackground, whiteSpace: 'pre-wrap' }}>
                                            {showFullNotes || notesContent.length <= 100 ? (
                                                <>
                                                    {notesContent}
                                                    {notesContent.length > 100 && (
                                                        <span 
                                                            onClick={() => setShowFullNotes(false)}
                                                            style={{ 
                                                                color: palette.primary || '#007bff', 
                                                                cursor: 'pointer', 
                                                                textDecoration: 'underline', 
                                                                fontSize: '0.75rem', 
                                                                display: 'inline-block', 
                                                                marginLeft: '4px',
                                                                fontWeight: 'normal'
                                                            }}
                                                            role="button"
                                                            tabIndex={0}
                                                            onKeyPress={(e) => e.key === 'Enter' && setShowFullNotes(false)}
                                                        >
                                                            Nascondi
                                                        </span>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {notesContent.substring(0, 100)}...
                                                    <span 
                                                        onClick={() => setShowFullNotes(true)}
                                                        style={{ 
                                                            color: palette.primary || '#007bff', 
                                                            cursor: 'pointer', 
                                                            textDecoration: 'underline', 
                                                            fontSize: '0.75rem', 
                                                            display: 'inline-block', 
                                                            marginLeft: '4px',
                                                            fontWeight: 'normal'
                                                        }}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyPress={(e) => e.key === 'Enter' && setShowFullNotes(true)}
                                                    >
                                                        Mostra tutto
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}
                            
                            {jobData.contactEmail && <DetailItem label="Email Referente" value={jobData.contactEmail} palette={palette} />}
                            
                            {/* Totale Ore Lavorate Commessa */}
                            {(totalWorkTime.hours > 0 || totalWorkTime.minutes > 0) && (
                                <DetailItem
                                    label="Totale Ore Lavorate Commessa"
                                    value={`${totalWorkTime.hours}h ${totalWorkTime.minutes}min`}
                                    palette={palette}
                                />
                            )}

                            {/* Riepilogo Ore Lavorate per Utente */}
                            {userWorkSummary.length > 0 && (
                                <div className="md:col-span-2 mt-3 pt-3 border-t" style={{borderColor: palette.neutralLighter || '#e0e0e0'}}>
                                    <h4 className="text-base font-medium mb-2" style={{ color: palette.text }}>Riepilogo Ore Lavorate per Utente:</h4>
                                    <div className="space-y-1">
                                        {userWorkSummary.map(user => (
                                            <div key={user.userId} className="text-sm p-1.5 rounded" style={{ backgroundColor: palette.neutralLightest || '#f5f5f5' }}>
                                                <div className="flex justify-between items-center">
                                                    <span style={{ color: palette.text }}>{user.userLabel}</span>
                                                    <span className="font-semibold" style={{ color: palette.primary || '#007bff' }}>{user.totalHours}h {user.totalMinutes}min</span>
                                                </div>
                                                {user.closingStatuses && Object.keys(user.closingStatuses).length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {Object.entries(user.closingStatuses).map(([status, count]) => (
                                                            <span 
                                                                key={status} 
                                                                className="text-xs px-2 py-0.5 rounded-full"
                                                                style={{ 
                                                                    backgroundColor: '#17a2b8', 
                                                                    color: 'white'
                                                                }}
                                                            >
                                                                {status} {count > 1 ? `(${count})` : ''}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {jobData.notes && (
                                <div className="md:col-span-2">
                                    <DetailItem label="Note" value={jobData.notes} palette={palette} isExpandable={true} />
                                </div>
                            )}
                        </div> {/* Chiusura del div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4" */}

                        {/* Sezione bottoni */}
                        <div className="mt-4 p-4" style={{ backgroundColor: palette.footerBg, borderTop: '1px solid #eee' }}>
                            {/* Stile grid per pulsanti uniformi */}
                            <div className="grid grid-cols-2 gap-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
                                {/* Bottoni specifici per la vista calendario */}
                                {calendarView && appointmentId && (
                                    <>
                                        <button 
                                            onClick={handleEditAppointment}
                                            className="flex items-center justify-center gap-1 py-3 px-3 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out w-full"
                                            style={{
                                                backgroundColor: palette.warningLight || '#fff3cd',
                                                color: palette.warning || '#856404',
                                                border: `1px solid ${palette.warning || '#ffeeba'}`,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                height: '56px'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = palette.warning || '#ffeeba'; }}
                                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = palette.warningLight || '#fff3cd'; }}
                                        >
                                            <FontAwesomeIcon icon={faPencil} /> Modifica Appuntamento
                                        </button>
                                        <button 
                                            onClick={handleDeleteAppointment}
                                            className="flex items-center justify-center gap-1 py-3 px-3 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out w-full"
                                            style={{
                                                backgroundColor: palette.errorLight || '#f8d7da',
                                                color: palette.error || '#721c24',
                                                border: `1px solid ${palette.error || '#f5c6cb'}`,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                height: '56px'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = palette.error || '#f5c6cb'; e.currentTarget.style.color = '#fff'; }}
                                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = palette.errorLight || '#f8d7da'; e.currentTarget.style.color = palette.error || '#721c24'; }}
                                        >
                                            <FontAwesomeIcon icon={faTrash} /> Elimina Appuntamento
                                        </button>
                                        <button 
                                            onClick={handleEditClick} 
                                            className="flex items-center justify-center gap-1 py-3 px-3 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out w-full"
                                            style={{
                                                backgroundColor: palette.primary, 
                                                color: palette.textOnPrimary,
                                                border: `1px solid ${palette.primaryHover}`,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                height: '56px'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = palette.primaryHover; }}
                                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = palette.primary; }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-pencil" /> Modifica
                                        </button>
                                        <button 
                                            onClick={handleScheduleClick}
                                            className="flex items-center justify-center gap-1 py-3 px-3 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out w-full"
                                            style={{
                                                backgroundColor: '#28a745',
                                                color: '#ffffff',
                                                border: `1px solid #1e7e34`,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                height: '56px'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#1e7e34'; }}
                                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#28a745'; }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-calendar" /> Pianifica
                                        </button>
                                        <button
                                            onClick={() => dispatch(closeModal())}
                                            className="flex items-center justify-center gap-1 py-3 px-3 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out w-full"
                                            style={{
                                                backgroundColor: '#6c757d',
                                                color: '#ffffff',
                                                border: `1px solid #5a6268`,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                height: '56px'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#5a6268'; }}
                                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#6c757d'; }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-times" /> Chiudi
                                        </button>
                                    </>
                                )}
                                
                                {/* Pulsanti quando non siamo in vista calendario */}
                                {(!calendarView || !appointmentId) && (
                                    <>
                                        <button 
                                            onClick={handleEditClick} 
                                            className="flex items-center justify-center gap-1 py-3 px-3 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out w-full"
                                            style={{
                                                backgroundColor: palette.primary, 
                                                color: palette.textOnPrimary,
                                                border: `1px solid ${palette.primaryHover}`,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                height: '56px'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = palette.primaryHover; }}
                                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = palette.primary; }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-pencil" /> Modifica
                                        </button>
                                        <button 
                                            onClick={handleScheduleClick}
                                            className="flex items-center justify-center gap-1 py-3 px-3 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out w-full"
                                            style={{
                                                backgroundColor: '#28a745',
                                                color: '#ffffff',
                                                border: `1px solid #1e7e34`,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                height: '56px'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#1e7e34'; }}
                                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#28a745'; }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-calendar" /> Pianifica
                                        </button>
                                        <button
                                            onClick={() => dispatch(closeModal())}
                                            className="flex items-center justify-center gap-1 py-3 px-3 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out w-full"
                                            style={{
                                                backgroundColor: '#6c757d',
                                                color: '#ffffff',
                                                border: `1px solid #5a6268`,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                height: '56px'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#5a6268'; }}
                                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#6c757d'; }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-times" /> Chiudi
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div> {/* Chiusura del div className="bg-white shadow-xl rounded-lg overflow-hidden flex flex-col gap-y-6 w-full xl:w-1/3 h-fit" */}

                {/* Colonna Destra - Tabs */}
                <div className="w-full xl:w-2/3">
                    <Folder 
                        tabTitleArray={tabTitles} 
                        palette={palette} 
                        initialTab={initialTab} 
                        onTabChange={handleTabChange} 
                    >
                        {tabContents}
                    </Folder>
                </div>
            </div>


        </>
    );
}
